import "./instrument"
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react"
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import './index.css'
import App from './App.tsx'

// PWA: registra o SW (em dev para testar push; em prod para PWA e updates)
// Não faz reload automático - evita deslogar o usuário no meio de uma tarefa.
// O usuário escolhe quando atualizar; localStorage (auth) persiste no reload.
import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        import('@/stores/update.store').then(({ useUpdateStore }) => {
          useUpdateStore.getState().setNeedRefresh(updateSW)
        })
      },
      onOfflineReady() {
        // App pronto para uso offline - sem ação necessária
      },
      // Verificação periódica: o navegador só checa na carga. Com isso, checamos a cada 30 min e ao voltar o foco.
      onRegisteredSW(swUrl, registration) {
        if (!registration) return
        const checkForUpdates = async () => {
          if (registration.installing || !navigator.serviceWorker) return
          if ('connection' in navigator && !navigator.onLine) return
          try {
            const resp = await fetch(swUrl, {
              cache: 'no-store',
              headers: { 'cache-control': 'no-cache' },
            })
            if (resp?.status === 200) await registration.update()
          } catch {
            // Ignorar erros de rede (offline, etc)
          }
        }
        // Checar após 30s (captura deploy feito logo antes de abrir o app)
        setTimeout(checkForUpdates, 30 * 1000)
        // Checar a cada 5 minutos (mais frequente para detectar atualizações)
        setInterval(checkForUpdates, 5 * 60 * 1000)
        // Checar quando o app volta ao foco (ex: usuário troca de aba e volta)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdates()
        })
      },
    })
  })

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 min
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: true,
    },
  },
})

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'lizta-react-query',
  throttleTime: 1000,
})

// Não aplicar tema aqui - será aplicado pelo theme.store quando o perfil do usuário for carregado
// Isso evita flash de tema incorreto

const container = document.getElementById('root')!
const root = createRoot(container, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("Uncaught error", error, errorInfo?.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
})
root.render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        dehydrateOptions: {
          // Persistir só o que faz sentido (ex.: listas públicas)
          shouldDehydrateQuery: (query) => {
            const key = query.queryKey[0]
            if (key !== 'publicGames' && key !== 'participantStatus') return false
            // Não persistir queries pendentes - causam erro "promise.then is not a function" no restore
            return query.state.status === 'success'
          },
        },
      }}
    >
      <App />
    </PersistQueryClientProvider>
  </StrictMode>,
)
